import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { ServerMessage, BatchUpdateEvent, JournalEntry } from '@roll1/shared';
import {
  SHEET_RP_MSG,
  type SheetRpField,
  type SheetRpListChangedEvent,
  type HudBoardConfig,
} from '@roll1/shared';
import { useSessionContext } from '../contexts/SessionContext';
import { useChatContext } from '../contexts/ChatContext';
import { useInitiativeContext } from '../contexts/InitiativeContext';
import { useSheetContext } from '../contexts/SheetContext';
import { useAlertContext } from '../contexts/AlertContext';
import { useJournalContext } from '../contexts/JournalContext';
import { useI18n } from '../i18n';
import { usePageLoader } from './usePageHydration';

/**
 * Session-lifetime sync for the stores that live ABOVE the StudioGate
 * table ⇄ GM Studio fork (App.tsx): join hydration + the WS routes of the
 * campaign materials — journal, character sheets (incl. rp echoes), sheet
 * templates, the page list, batch reconciles.
 *
 * Before, both lived in AppShell (useMultiplayerSync) and Studio kept its own
 * partial copy of the routes. The stores outlive either surface, their
 * subscriptions and hydration did not:
 *  - every AppShell mount re-applied the JOIN snapshot (per-mount "initialized"
 *    ref), so Studio → table wiped everything created or changed since join;
 *  - echoes the mounted surface did not route (sheet:updated in Studio, …)
 *    never reached the store.
 * Now each store has exactly one subscription and one hydration per join
 * (`mp.joinSeq`: first join, real reconnect), whichever surface is shown.
 * A new campaign remounts this provider subtree (ErrorBoundary key).
 *
 * Canvas-bound routes (tokens / drawing / fog / notes / pins of the viewed
 * page) and table UI side-effects (toasts, panels) stay with the surface that
 * shows the canvas — useMultiplayerSync / Studio map. Page activation is
 * routed here: which page the players see must be right on both surfaces.
 */
export function useSessionStoreSync(): void {
  const { mp, pages, setSessionSettings, gameSystem } = useSessionContext();
  const { initiative } = useInitiativeContext();
  const { chat } = useChatContext();
  const { sheets: charSheets, templates: tmpl } = useSheetContext();
  const alertCtx = useAlertContext();
  const journal = useJournalContext();
  const { t } = useI18n();
  const { loadPageToHooks, saveCurrentPageToStore } = usePageLoader();

  // Apply the server snapshot once per join (0 = nothing applied yet).
  const appliedJoinSeqRef = useRef(0);
  useEffect(() => {
    const s = mp.serverState;
    if (!s || mp.joinSeq === appliedJoinSeqRef.current) return;
    const firstJoin = appliedJoinSeqRef.current === 0;
    appliedJoinSeqRef.current = mp.joinSeq;
    // The canvas hooks above the fork are the table's: reload them only while
    // the table holds them. Released (Studio shown, or before the first table
    // mount) they stay released — nothing would keep them live, and a later
    // save (page:activated) would write them over the fresh store. The table
    // loads its page from the store when it mounts.
    const tableHoldsHooks = pages.hydratedPageIdRef.current !== '';
    const activePage = pages.initFromState(s);
    if (activePage && tableHoldsHooks) loadPageToHooks(activePage);
    if (s.initiative) {
      initiative.applyState(s.initiative);
    }
    if (s.characterSheets) {
      charSheets.setSheets(s.characterSheets);
    }
    if (s.settings) {
      setSessionSettings(s.settings);
    }
    if (s.alerts) {
      alertCtx.setAlerts(s.alerts);
    }
    if (s.chat?.length) {
      chat.initMessages(s.chat);
    }
    // Load journal entries from initial join data
    if (mp.initialJournal) {
      // F-4: the snapshot carries the links apart from the entries; they are laid on the entries
      // (linksFrom / linksTo) — after a reload they are not lost.
      const links = mp.initialJournal.links ?? [];
      journal.setEntries(
        links.length === 0
          ? mp.initialJournal.entries
          : mp.initialJournal.entries.map((e) => ({
              ...e,
              linksFrom: links.filter((l) => l.sourceId === e.id),
              linksTo: links.filter((l) => l.targetId === e.id),
            })),
      );
    }
    // Once per session tree, not per surface mount (AppShell used to add it
    // again on every return from Studio).
    if (firstJoin) {
      chat.addSystemMessage(t('chat.welcome'));
    }
  }, [mp.joinSeq, mp.serverState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return mp.onMessage((msg: ServerMessage) => {
      // ── Granular Roleplay broadcasts (Feature B-01) ─────────────
      // Defined in shared/contracts/sheet.ts, not part of the legacy
      // ServerMessage union — narrow on the string type. rp echoes mirror into
      // the per-sheet charSheets store: the adapter-mode HUD derives its rp
      // view from sheet.values.roleplay_json, so this is the only route that
      // updates a live HUD on every client that receives the echo (GM + owner).
      // applyRpMutation preserves sheet identity on no-op echoes.
      const rpType = (msg as { type: string }).type;
      if (rpType === SHEET_RP_MSG.fieldUpdated) {
        const m = msg as unknown as { sheetId: string; field: SheetRpField; value: string };
        charSheets.handleRpFieldUpdated(m.sheetId, m.field, m.value);
        return;
      }
      if (rpType === SHEET_RP_MSG.listChanged) {
        const m = msg as unknown as SheetRpListChangedEvent;
        if (m.op === 'add') {
          charSheets.handleRpListChanged(m.sheetId, m.listKey, 'add', { item: m.item as Record<string, unknown> });
        } else if (m.op === 'update') {
          charSheets.handleRpListChanged(m.sheetId, m.listKey, 'update', { itemId: m.itemId, changes: m.changes as Record<string, unknown> });
        } else {
          charSheets.handleRpListChanged(m.sheetId, m.listKey, 'remove', { itemId: m.itemId });
        }
        return;
      }
      if (rpType === SHEET_RP_MSG.boardConfigUpdated) {
        const m = msg as unknown as { sheetId: string; config: HudBoardConfig };
        charSheets.handleRpBoardConfigUpdated(m.sheetId, m.config as unknown as Record<string, unknown>);
        return;
      }

      switch (msg.type) {
        // Page list (the page CONTENT of the viewed page is canvas-bound)
        case 'page:list': pages.handlePageList(msg.pages, msg.activePageId); break;
        case 'page:created': pages.handlePageCreated(msg.page); break;
        case 'page:snapshot-created': pages.handleSnapshotCreated(msg.page, msg.pageState, msg.fromPageId); break;
        case 'page:renamed': pages.handlePageRenamed(msg.pageId, msg.name); break;
        case 'page:owner-changed': pages.handlePageOwnerChanged(msg.pageId, msg.owner); break;
        case 'page:parent-changed': pages.handlePageParentChanged(msg.pageId, msg.parentPageId); break;
        case 'page:deleted':
          // T-17 (3): the map the GM was looking at alone is gone — back on the players' map, and the GM is told.
          if (pages.isPreview && pages.viewingPageId === msg.pageId) toast.info(t('mapView.viewedDeleted'));
          pages.handlePageDeleted(msg.pageId);
          break;
        case 'page:reordered': pages.handlePageReordered(msg.pageIds); break;
        // Which page the players see is campaign state, not the table's: in
        // Studio it went unrouted and «players see» stayed stale until rejoin.
        // A viewer on the players' page follows; the canvas of the surface
        // loads the new page through its own viewingPageId switch.
        case 'page:activated':
          saveCurrentPageToStore();
          pages.handlePageActivated(msg.pageId, msg.pageState);
          break;
        // Character sheets
        case 'sheet:created': charSheets.handleCreated(msg.sheet); break;
        case 'sheet:updated': charSheets.handleUpdated(msg.sheetId, msg.changes, msg.abilities); break;
        case 'sheet:deleted': charSheets.handleDeleted(msg.sheetId); break;
        case 'sheet:group-updated': charSheets.setSheets(prev => prev.map(sh => sh.id === msg.sheetId ? { ...sh, group: msg.group || undefined } : sh)); break;
        case 'sheet:visibility-changed': charSheets.setSheets(prev => prev.map(sh => sh.id === msg.sheetId ? { ...sh, visible: msg.visible } : sh)); break;
        case 'sheet:holder-changed': charSheets.setSheets(prev => prev.map(sh => sh.id === msg.sheetId ? { ...sh, holder: msg.holder } : sh)); break;
        case 'sheet:owner-changed': charSheets.handleOwnerChanged(msg.sheetId, msg.ownerName, msg.ownerId); break;
        case 'ai-context:character-updated': charSheets.setSheets(prev => prev.map(s => s.id === msg.sheetId ? { ...s, aiContext: msg.aiContext } : s)); break;
        case 'sheet:public-fields-updated': charSheets.handlePublicFieldsUpdated(msg.sheetId, msg.publicFields, msg.visibleValues); break;
        case 'sheet:token-bound': charSheets.handleTokenBound(msg.sheetId, msg.tokenId); break;
        case 'sheet:token-unbound': charSheets.handleTokenUnbound(msg.sheetId); break;
        case 'sheet:repeating-row-added': charSheets.handleRepeatingRowAdded(msg.sheetId, msg.sectionKey, msg.row); break;
        case 'sheet:repeating-row-removed': charSheets.handleRepeatingRowRemoved(msg.sheetId, msg.sectionKey, msg.rowId); break;
        case 'sheet:repeating-row-updated': charSheets.handleRepeatingRowUpdated(msg.sheetId, msg.sectionKey, msg.rowId, msg.changes); break;
        case 'sheet:spell-slots-updated': if (gameSystem === 'dnd5e') charSheets.handleSpellSlotsUpdated(msg.sheetId, msg.spellSlots); break;
        case 'sheet:rest-completed': charSheets.handleRestCompleted(msg.sheetId, msg.restType, msg.spellSlots, msg.hp, msg.hitDice, msg.abilities); break;
        case 'sheet:hit-dice-result': if (gameSystem === 'dnd5e') charSheets.handleHitDiceResult(msg.sheetId, msg.newHp, msg.hitDiceUsed); break;
        case 'sheet:token-actions-updated': charSheets.handleTokenActionsUpdated(msg.sheetId, msg.tokenActions); break;
        case 'sheet:portrait-updated': charSheets.handlePortraitUpdated(msg.sheetId, msg.portrait); break;
        case 'sheet:token-description-updated': charSheets.handleTokenDescriptionUpdated(msg.sheetId, msg.tokenDescription); break;
        case 'sheet:effects-updated': charSheets.handleEffectsUpdated(msg.sheetId, msg.activeEffects, msg.values); break;
        case 'sheet:spellbook-updated': charSheets.handleSpellbookUpdated(msg.sheetId, msg.spellbook); break;
        case 'sheet:concentration-updated': if (gameSystem === 'dnd5e') charSheets.handleConcentrationUpdated(msg.sheetId, msg.concentrationSpellId); break;
        case 'sheet:level-up-complete': if (gameSystem === 'dnd5e') charSheets.handleLevelUpComplete(msg.sheetId, msg.values, msg.abilities, msg.hitDice, msg.spellSlots, msg.spellbook, msg.classData); break;
        case 'sheet:list': charSheets.setSheets(msg.sheets); break;
        // Sheet templates
        case 'template:created': tmpl.handleCreated(msg.template); break;
        case 'template:updated': tmpl.handleUpdated(msg.templateId, msg.changes); break;
        case 'template:deleted': tmpl.handleDeleted(msg.templateId); break;
        case 'template:list': tmpl.setTemplates(msg.templates); break;
        // Journal
        case 'journal:created': journal.handleCreated(msg.entry); break;
        case 'journal:updated': journal.handleUpdated(msg.entryId, msg.changes); break;
        case 'journal:deleted': journal.handleDeleted(msg.entryId); break;
        case 'journal:owner-changed': journal.handleOwnerChanged(msg.entryId, msg.owner); break;
        case 'journal:revealed': journal.handleRevealed(msg.entry); break;
        case 'journal:pinned': journal.handlePinned(msg.entryId, msg.pinned); break;
        case 'journal:personal-created': journal.handlePersonalCreated(msg.entry); break;
        case 'journal:linked': journal.handleLinked(msg.link); break;
        case 'journal:unlinked': journal.handleUnlinked(msg.sourceId, msg.targetId, msg.relation); break;
        case 'state:batch-update': {
          // ADR-008: Reconcile handler — must cover all domains used in batch scenarios.
          const bu = msg as BatchUpdateEvent;
          // Update page store for affected pages
          for (const pid of bu.affected.pages) {
            const freshPage = bu.state.pages.find(p => p.id === pid);
            if (freshPage) pages.setPageData(pid, freshPage);
          }
          // Reload the page the canvas hooks hold if it was affected
          const hydratedId = pages.hydratedPageIdRef.current;
          if (hydratedId && bu.affected.pages.includes(hydratedId)) {
            const currentPage = bu.state.pages.find(p => p.id === hydratedId);
            if (currentPage) loadPageToHooks(currentPage);
          }
          // Update character sheets
          if (bu.affected.sheets.length > 0) {
            charSheets.setSheets(bu.state.characterSheets);
          }
          // Journal full sync (undo): replace all entries with the restored set
          if ((bu as { journalFullSync?: boolean }).journalFullSync && bu.journalEntries) {
            journal.setEntries(bu.journalEntries as JournalEntry[]);
          } else if (bu.journalEntries?.length) {
            // Incremental: add new entries
            for (const entry of bu.journalEntries) {
              journal.handleRevealed(entry);
            }
          }
          break;
        }
      }
    });
  }, [mp, pages, charSheets, tmpl, journal, gameSystem, loadPageToHooks, saveCurrentPageToStore, t]);
}

/** Mount point (App.tsx), inside JournalProvider and above StudioGate. */
export function SessionStoreSync(): null {
  useSessionStoreSync();
  return null;
}
